/**
 * Email Parser Utility
 *
 * Parses .eml files to extract sender information and date for pre-filling
 * form fields. Uses postal-mime for browser-compatible RFC822 parsing.
 */

import PostalMime from "postal-mime"

/**
 * Parsed email data extracted from an .eml file.
 */
export interface ParsedEmailData {
  /** Sender's display name (may be empty if only email is present). */
  senderName: string
  /** Sender's email address. */
  senderEmail: string
  /** Date the email was sent (or undefined if not parseable). */
  date: Date | undefined
}

/**
 * Parse an .eml file and extract sender name and date.
 *
 * Uses postal-mime to parse the RFC822 email format. Extracts the "From"
 * header for sender information and the "Date" header for the send date.
 *
 * @param file - The .eml File object to parse.
 * @returns A promise resolving to the parsed email data.
 * @throws Error if the file cannot be read or parsed.
 */
export async function parseEmlFile(file: File): Promise<ParsedEmailData> {
  // Read the file content as an ArrayBuffer (postal-mime accepts ArrayBuffer or string)
  const arrayBuffer = await file.arrayBuffer()

  // Parse the email
  const parser = new PostalMime()
  const email = await parser.parse(arrayBuffer)

  // Extract sender information from the "from" field
  // postal-mime returns from as { name?: string, address: string }
  const fromAddress = email.from
  const senderName = fromAddress?.name ?? ""
  const senderEmail = fromAddress?.address ?? ""

  // Parse the date
  let date: Date | undefined
  if (email.date) {
    const parsed = new Date(email.date)
    // Check if the date is valid
    if (!isNaN(parsed.getTime())) {
      date = parsed
    }
  }

  return {
    senderName,
    senderEmail,
    date,
  }
}

/**
 * Check if a file is a supported email format (.eml).
 *
 * @param file - The file to check.
 * @returns True if the file has a .eml extension.
 */
export function isEmlFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".eml")
}
